import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  isSupported,
  needsIOSInstall,
  requestAndSaveToken,
  refreshTokenIfNeeded,
  deleteToken,
  listenForeground,
} from '../fcm';

function safePermission() {
  try {
    return typeof Notification !== 'undefined' ? Notification.permission : 'default';
  } catch {
    return 'default';
  }
}

/**
 * Returns push-notification state and controls.
 *
 * {
 *   permission   — 'default' | 'granted' | 'denied'
 *   token        — string | null
 *   isSupported  — boolean
 *   iosNeedsPWA  — boolean (on iOS but not installed yet)
 *   isBlocked    — boolean (user denied)
 *   subscribe    — async () => void  (call from a button click)
 *   unsubscribe  — async () => void
 * }
 */
export default function usePushNotifications() {
  const supported = isSupported();
  const iosNeedsPWA = needsIOSInstall();

  const [permission, setPermission] = useState(safePermission);
  const [token, setToken] = useState(() => localStorage.getItem('fcm_token'));
  const isBlocked = localStorage.getItem('fcm_blocked') === '1' || permission === 'denied';

  // On mount: if already granted, refresh the token and start foreground listener.
  useEffect(() => {
    if (!supported || permission !== 'granted') return;

    let cancelled = false;

    refreshTokenIfNeeded().then((t) => {
      if (!cancelled && t) setToken(t);
    });

    const unsubscribe = listenForeground((payload) => {
      console.log('[FCM] Foreground message:', payload);
      const title = payload.data?.title || payload.notification?.title;
      const body  = payload.data?.body  || payload.notification?.body || '';
      if (!title) return;

      // In-app toast notification (shown when the app is open).
      toast(
        (t) => (
          <div className="flex flex-col gap-0.5" onClick={() => toast.dismiss(t.id)}>
            <span className="font-semibold text-sm text-gray-900">{title}</span>
            {body && <span className="text-xs text-gray-600">{body}</span>}
          </div>
        ),
        {
          duration: 6000,
          icon: '🔔',
          style: { maxWidth: 360 },
        }
      );

      // Also show OS notification via service worker for completeness.
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((reg) => {
          reg.showNotification(title, {
            body,
            icon: '/android-chrome-192x192.png',
            badge: '/android-chrome-192x192.png',
            tag: 'canvassing-reminder',
            renotify: true,
          });
        });
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [supported, permission]);

  // subscribe() — must be triggered by a user gesture (button click).
  const subscribe = useCallback(async () => {
    if (!supported) return;
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        const t = await requestAndSaveToken();
        if (t) setToken(t);
      }
    } catch (err) {
      console.warn('[FCM] subscribe error:', err?.message);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    await deleteToken();
    setToken(null);
    setPermission(safePermission());
  }, []);

  return {
    permission,
    token,
    isSupported: supported,
    iosNeedsPWA,
    isBlocked,
    subscribe,
    unsubscribe,
  };
}
